import { Alert, Form, Input, Modal, Switch, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { addCompanyUserRole, getCompanyUserRoles } from "../repository/data.repository";
import { CompanyRole } from "../repository/data.default";
import { getProblemMessage, retry } from "../utils/http";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { InputObject } from "../controls/default";

export interface CompanyRoleCreateDialogProps {
  readonly open: boolean;
  readonly companyUid: string;
  readonly initialName?: string;
  readonly onClose: () => void;
  // Called with the saved role (with the real server uid) so the caller can
  // auto-select it and bump its refresh token for sibling comboboxes.
  readonly onCreated: (created: InputObject) => void;
}

// Admin brief 2026-11-04 — inline "Create new role" dialog used by the company
// registration wizard. Duplicate-name check runs client-side on open AND on
// submit against the current server list; the backend has a case-insensitive
// uniqueness guard that catches anything we missed (409 Conflict).
function CompanyRoleCreateDialog(props: CompanyRoleCreateDialogProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue({
        name: props.initialName ?? "",
        description: "",
        isManager: false,
      });
      setError("");
    } else {
      form.resetFields();
      setError("");
    }
  }, [props.open, props.initialName, form]);

  async function checkDuplicate(name: string): Promise<boolean> {
    // Lightweight server lookup — we scope to the same company + search by
    // prefix, then compare normalized names client-side.
    try {
      const { data } = await retry(() =>
        getCompanyUserRoles({
          searchQuery: {
            conditions: [
              {
                field: nameOf<CompanyRole>("companyUid"),
                operation: QueryOperation.Equal,
                values: [props.companyUid],
              },
              {
                field: nameOf<CompanyRole>("name"),
                operation: QueryOperation.StartsWith,
                values: [name],
              },
            ],
          },
          pageNumber: 0,
          pageSize: 100,
        }),
      );
      const target = name.trim().toUpperCase();
      return (data ?? []).some((r) => (r.normalizedName ?? r.name).toUpperCase() === target);
    } catch {
      // On lookup failure we fall through to the server-side guard.
      return false;
    }
  }

  async function onSubmit(): Promise<void> {
    try {
      const v = await form.validateFields();
      const name = (v.name ?? "").trim();
      setError("");
      setSaving(true);
      if (await checkDuplicate(name)) {
        setSaving(false);
        setError(`A role named "${name}" already exists in this company.`);
        return;
      }
      const created = await addCompanyUserRole({
        name,
        description: v.description || "",
        isManager: !!v.isManager,
        companyUid: props.companyUid,
      } as any);
      message.success(`Role "${created.name}" created.`);
      setSaving(false);
      props.onCreated({
        display: created.name,
        value: created.uid,
        dataItem: created,
      });
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      const msg = getProblemMessage(err) || "Failed to create role.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={props.open}
      title="Create role"
      okText="Create"
      onCancel={() => props.onClose()}
      onOk={onSubmit}
      confirmLoading={saving}
      destroyOnClose
      // Keep above any open Select dropdown the dialog was opened from.
      zIndex={2000}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, message: "Name is required" },
            { min: 2, message: "Name must be at least 2 characters" },
            { max: 100, message: "Name is too long" },
            {
              validator: (_, value) => {
                const v = (value ?? "").trim();
                if (!v) return Promise.resolve();
                // Reserved names that would collide with the platform-level roles.
                const reserved = ["admin", "platform admin", "super admin"];
                if (reserved.includes(v.toLowerCase()))
                  return Promise.reject(
                    new Error(`"${v}" is reserved for the platform.`),
                  );
                return Promise.resolve();
              },
            },
          ]}
          normalize={(value) => (typeof value === "string" ? value.trimStart() : value)}
        >
          <Input autoFocus />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description (optional)"
          rules={[{ max: 500, message: "Description is too long" }]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item
          name="isManager"
          valuePropName="checked"
          label="Is manager role"
          tooltip="Managers lead a department / team. Plain employee roles do not."
        >
          <Switch />
        </Form.Item>
        {error && (
          <Alert type="error" message={error} className="mt-2" showIcon />
        )}
        <Typography.Text type="secondary" className="text-xs">
          This role is saved to the current company and will be available in
          every user's Role dropdown in this wizard.
        </Typography.Text>
      </Form>
    </Modal>
  );
}

export default CompanyRoleCreateDialog;
